import React from 'react';
import { Box, Stack, styled } from '@mui/material';
import { useSearchParams } from 'react-router';
import NetworkError from 'assets/images/ErrorState/NetworkError.svg';
import { ContainerLayout } from 'components/ContainerLayout/ContainerLayout';
import { CareProgramsList } from 'components/CareProgramsList/CareProgramsList';
import { CareProgramDetailPage } from 'components/CareProgramDetailPage/CareProgramDetailPage';
import { ErrorState } from 'components/ErrorState/ErrorState';
import { useLayoutElementsHeight } from 'hooks/useLayoutElementsHeight';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useCareProgramsByTopic, useCareProgramRequest } from 'features/resources/hooks';
import { getHealthTopicTranslationKey } from 'features/resources/utils/healthTopicUtils';
import type { HealthTopic } from 'features/resources/types';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { CareProgramsIconBanner } from './CareProgramsListHeader';

export interface CareProgramsListPageProps {
  healthTopic?: HealthTopic;
  onBack?: () => void;
}

const ScrollableContainerBase = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.brandBackgroundPrimary.main,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehaviorY: 'none',
  WebkitOverflowScrolling: 'touch',
}));

const CenteredState = styled(Stack)(() => ({
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const CareProgramsListPage: React.FC<CareProgramsListPageProps> = ({
  healthTopic,
  onBack,
}) => {
  const { translation } = useTranslationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { commonContentHeight } = useLayoutElementsHeight();
  const { isMobile, isTablet } = useDeviceType();

  // Get topicId from URL params or prop
  const topicId = searchParams.get('topicId') as HealthTopic;
  const resolvedTopic = healthTopic ?? topicId;
  const topicTitle = translation(getHealthTopicTranslationKey(resolvedTopic));

  const {
    error,
    refetch,
    selectedProgram,
    programDetail,
    isDetailModalOpen,
    enrollmentStatus,
    handleProgramClick,
    handleModalClose,
  } = useCareProgramRequest();

  const { groupedProgramsBySections } = useCareProgramsByTopic({ topic: resolvedTopic });

  return (
    <ContainerLayout>
      <ScrollableContainerBase
        role="main"
        className="care-programs-list-page"
        sx={{ height: commonContentHeight }}
      >
        <PageHeader title={topicTitle} isBackVisible onBack={() => navigate(`/resources/topics`)} />
        {error ? (
          <CenteredState>
            <ErrorState
              imageArray={[NetworkError]}
              onRetryClick={refetch}
              title={translation('no_connection_overlay')}
              subtitle={translation('error_loading_body')}
            />
          </CenteredState>
        ) : (
          <>
            <CareProgramsIconBanner healthTopic={resolvedTopic} />
            <CareProgramsList
              sections={groupedProgramsBySections}
              onProgramClick={handleProgramClick}
              selectedProgramId={selectedProgram?.id}
            />
            {programDetail && (
              <CareProgramDetailPage
                program={programDetail}
                open={isMobile || isTablet ? isDetailModalOpen : !!selectedProgram}
                onClose={handleModalClose}
                enrollmentRequested={
                  enrollmentStatus?.hasRequestPending || enrollmentStatus?.isEnrolled
                }
              />
            )}
          </>
        )}
      </ScrollableContainerBase>
    </ContainerLayout>
  );
};
